// NPM
import { isEmpty, omitBy, isNil } from "lodash";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import { encode } from "Helpers/index";
import "./delivery-lists.scss";
import { Col, Row } from "antd";
import {
  ActionMenu,
  Button,
  DataTable,
  SelectInput,
} from "@life-line-lab/components-react";
import { MoreOutlined } from "@ant-design/icons";
import DataTableFilter from "Components/shared/data-table-filter";
import { FilterSelectionProps } from "Components/shared/data-table-filter/data-table-filter";

interface ServicePointListProps {
  offset: number;
  list?: any;
  settings?: any;
  onFetchLines: () => void;
  onFetchTypes: () => void;
  onFilter?: (filterInput) => void;
  onUpdateStatus: (value: string, data: any) => void;
}

const ServicePointList: React.FC<ServicePointListProps> = ({
  offset,
  list,
  settings,
  onFilter,
  onFetchLines,
  onFetchTypes,
  onUpdateStatus,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const initialFilters: any = {
    orFields: "",
    byType: "",
    byLine: "",
  };

  const [filters, setFilters] = useState<{ [key: string]: string | boolean }>(
    initialFilters,
  );

  const dataTableFilters: FilterSelectionProps[] = [
    {
      key: "byType",
      name: "byType",
      label: t("label.filterByType"),
      placeholder: t("placeholder.filterByType"),
      value: filters.byType,
      options: [{ label: t("option.all"), value: "" }].concat(
        settings?.TYPE?.map((type) => {
          return {
            label: type.name,
            value: type.name,
          };
        }) || [],
      ),
    },
    {
      key: "byLine",
      name: "byLine",
      label: t("label.filterByLine"),
      placeholder: t("placeholder.filterByLine"),
      value: filters.byLine,
      options: [{ label: t("option.all"), value: "" }].concat(
        settings?.LINE?.map((line) => {
          return {
            label: line.name,
            value: line.name,
          };
        }) || [],
      ),
    },
  ];

  const columns = [
    {
      title: t("label.no.").toLocaleUpperCase(),
      dataIndex: "index",
      render: (_: any, record: any, index: number) => (
        <span>{index + 1 + offset}</span>
      ),
    },
    {
      title: t("label.pointID").toLocaleUpperCase(),
      dataIndex: "code",
    },
    {
      title: t("label.locationName").toLocaleUpperCase(),
      dataIndex: "locationName",
    },
    {
      title: t("label.type").toLocaleUpperCase(),
      dataIndex: "pointType",
      render: (_: any, record: any) =>
        record?.pointType?.map((type) => (
          <span>
            {type.name} <br />
          </span>
        )),
    },
    {
      title: t("label.address").toLocaleUpperCase(),
      dataIndex: "address",
      render: (_: any, record: any) => (
        <span>
          {record.address?.street1 + ", "}
          {record.address?.street2 ? record.address.street2 + ", " : ""}
          {record.address?.postcode + " "}
          {record.address?.city + ", "}
          {record.address?.state}
        </span>
      ),
    },
    {
      title: t("label.line").toLocaleUpperCase(),
      dataIndex: ["lines"],
      render: (data: any[]) => {
        return data?.length > 0 && (
          <>
            {data.map((line, index) => (
              <>
                <span>{line.name}</span>
                {index !== data.length - 1 && <br />}
              </>
            ))}
          </>
        );
      }
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "active",
      render: (_: any, record: any) => (
        <SelectInput
          className={`select select--${`${record.active}`.toLowerCase()}`}
          value={`${record.active}`}
          options={[
            {
              label: "ACTIVE",
              value: "true",
            },
            {
              label: "INACTIVE",
              value: "false",
            },
          ]}
          onChange={(value) => onUpdateStatus(value as any, record)}
        />
      ),
    },
    {
      title: "",
      dataIndex: "action",
      width: 40,
      render: (_: any, record: any) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.edit"),
              onClick: () => {
                handleUpdateServicePoint(record._id);
              },
            },
          ]}
        />
      ),
    },
  ];

  useEffect(() => {
    onFetchLines();
    onFetchTypes();
  }, []);

  useEffect(() => {
    if (onFilter && !isEmpty(filters)) {
      onFilter(omitBy(filters, (input) => isNil(input) || isEmpty(input)));
    }
  }, [filters]);

  const handleSearch = async (text: string) => {
    setFilters((filters) => ({ ...filters, orFields: text }));
  };

  const handleFilter = (selection: any) => {
    setFilters((filters) => ({
      ...filters,
      byType: selection.byType,
      byLine: selection.byLine,
    }));
  };

  const handleUpdateServicePoint = (id: string) => {
    const encodedID = encode(id);
    history.push(
      `/delivery-management/settings/service-points/form/1?id=${encodedID}`,
    );
  };

  const handleCreateServicePoint = () => {
    history.push(`/delivery-management/settings/service-points/form/1`);
  };

  return (
    <div className="delivery-list service-point">
      <DataTable
        rowKey={(record: any) => record._id}
        columns={columns as any}
        dataSource={list || []}
        scroll={{ x: true }}
        title={() => (
          <Row className="title-row" justify="space-between" align="middle">
            <Col className="mb-0">
              <DataTableFilter
                searchPlaceholder={t("label.searchByNameLocationType")}
                onSearch={(text: string) => handleSearch(text)}
                onFilter={(selected) => handleFilter(selected)}
                renderFilterSelection={dataTableFilters}
              />
            </Col>
            <Col className="mb-0">
              <Button
                label={t(`action.addServicePoint`)}
                type="primary"
                onClick={handleCreateServicePoint}
              />
            </Col>
          </Row>
        )}
      />
    </div>
  );
};

export default ServicePointList;
